import React from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import Row from '../../../core/layout/Row';
import Column from '../../../core/layout/Column';
import TextInput from '../../../core/form/TextInput';
import FormButtons from '../../../core/form/FormButtons';
import FetchSelect from '../../../core/form/FetchSelect';
import Button from '../../../core/form/Button';
import Table from '../../../core/form/Table';
import { CONTRACT_PRODUCTS, realCurrencyFormatter } from '../../../core/consts';
import Divider from '../../../core/layout/Divider';
import LocalFileInput from '../../../core/form/LocalFileInput';
import ErrorText from '../../../core/layout/ErrorText';
import Switch from '../../../core/form/Switch';
import CheckoutLinkProductsModalContainer from './CheckoutLinkProductsModalContainer';

const CheckoutLinkForm = (props) => (
  <form
    onSubmit={(event) => {
      event.preventDefault();
      props.onSubmit();
    }}
  >
    <Row>
      <Column size={4}>
        <TextInput
          required
          disabled={props.submitting}
          label="Customer"
          value={get(props.values, 'customer', '')}
          onChange={(value) => props.onChange('customer', value)}
          errorText={get(props.errors, 'customer', null)}
        />
      </Column>
      <Column size={4}>
        <TextInput
          required
          disabled={props.submitting}
          label="Customer Email"
          value={get(props.values, 'email', '')}
          onChange={(value) => props.onChange('email', value)}
          errorText={get(props.errors, 'email', null)}
        />
      </Column>
      <Column size={2}>
        <FetchSelect
          showSearch
          required
          url="/countries"
          disabled={true}
          label="Country"
          value={get(props.values, 'country', '')}
          onChange={(segment) => props.onChange('country', segment)}
          errorText={get(props.errors, 'country', '')}
          resultTransformer={{
            text: 'name',
            value: 'id',
          }}
        />
      </Column>
    </Row>
    <Row>
      <Column size={2}>
        <TextInput
          required
          type="number"
          disabled={props.submitting}
          label="Hubspot Deal ID"
          value={get(props.values, 'hubspotDealId', '')}
          onChange={(value) => props.onChange('hubspotDealId', value)}
          errorText={get(props.errors, 'hubspotDealId', null)}
        />
      </Column>
      <Column size={2}>
        <FetchSelect
          showSearch
          required
          url="/segments"
          disabled={props.submitting}
          label="Segment"
          value={get(props.values, 'segment', '')}
          onChange={(segment) => props.onChange('segment', segment)}
          errorText={get(props.errors, 'segment', '')}
          resultTransformer={{
            text: 'name',
            value: 'id',
          }}
        />
      </Column>
      <Column size={2}>
        <LocalFileInput
          value={get(props.values, 'contractFile', null) || get(props.values, 'contractUrl', null)}
          onChange={(value) => props.onChange('contractFile', value)}
          label="Upload the contract"
          accept="pdf"
          disabled={props.submitting || props.loading}
        />
      </Column>
    </Row>
    <Divider title="Products" />
    <CheckoutLinkProductsModalContainer />
    <Row>
      <Column size={12}>
        <Table
          columns={[
            {
              label: 'Product',
              path: 'description',
            },
            {
              label: 'Amount',
              path: 'amount',
              width: '150px',
            },
            {
              label: 'Free amount',
              path: 'freeAmount',
              width: '150px',
              render: (cell, row) => (row.hs_sku === CONTRACT_PRODUCTS.CUSTOM_PACKAGE ? '-' : cell),
            },
            {
              label: 'Unit Price',
              path: 'unitPrice',
              width: '150px',
              render: (cell) => realCurrencyFormatter.format(cell),
            },
            {
              label: 'Discount',
              path: 'discount',
              width: '100px',
              render: (cell) => (!!cell ? `${cell}%` : '-'),
            },
            {
              label: 'Actions',
              width: '150px',
              render: (cell, row) => {
                return (
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-around',
                      alignItems: 'center',
                      height: '100%',
                    }}
                  >
                    {![CONTRACT_PRODUCTS.CANCELLATION_FINE].includes(row.hs_sku) && (
                      <Button
                        icon="edit"
                        disabled={props.submitting || !row.isEnabled}
                        onClick={() => {
                          props.onEditProduct(row.hs_sku);
                        }}
                      />
                    )}
                    {![
                      CONTRACT_PRODUCTS.CUSTOM_PACKAGE,
                      CONTRACT_PRODUCTS.PLACEMENT_TEST_COMERCIAL,
                      CONTRACT_PRODUCTS.CANCELLATION_FINE,
                    ].includes(row.hs_sku) && (
                      <div style={{ height: '40px' }}>
                        <Switch
                          label=""
                          titleOff="Disabled"
                          titleOn="Enabled"
                          onChange={() => props.onToggleProduct(row.hs_sku)}
                          value={get(row, 'isEnabled', false)}
                          disabled={props.submitting || props.loading || props.disabled}
                        />
                      </div>
                    )}
                  </div>
                );
              },
            },
          ]}
          rows={get(props.values, 'products', [])}
          rowKey="hs_sku"
          showTableCount={false}
        />
        {get(props.errors, 'products', false) && <ErrorText>{get(props.errors, 'products', false)}</ErrorText>}
      </Column>
    </Row>
    <div style={{ display: 'flex', justifyContent: 'flex-end', margin: '10px 0px' }}>
      <h3>Total: {realCurrencyFormatter.format(props.totalPrice)}</h3>
    </div>
    <FormButtons
      confirmLabel={props.values.id ? 'Update Checkout link' : 'Create Checkout link'}
      isDisabled={props.submitting || !props.isDirty()}
      isSubmitting={props.submitting}
      onReset={props.onReset}
    />
  </form>
);

CheckoutLinkForm.propTypes = {
  onSubmit: PropTypes.func,
  onReset: PropTypes.func,
  values: PropTypes.object,
  productValues: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  onEditProduct: PropTypes.func.isRequired,
  onDeleteProduct: PropTypes.func.isRequired,
  onToggleProduct: PropTypes.func.isRequired,
  errors: PropTypes.object,
  submitting: PropTypes.bool,
  isDirty: PropTypes.func,
  totalPrice: PropTypes.number,
};

CheckoutLinkForm.defaultProps = {
  values: {},
  errors: {},
  submitting: false,
  totalPrice: 0,
  isDirty: () => false,
  onSubmit: () => alert('submitted'),
  onReset: () => false,
};

export default CheckoutLinkForm;
